import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useRestaurants } from '@/hooks/useRestaurants';
import { useAuditItems, useAuditStats } from '@/hooks/useAuditItems';
import { AuditItemsList } from '@/components/audit/AuditItemsList';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Target,
  Zap,
  Clock,
} from 'lucide-react';

export const AuditPageV2: React.FC = () => {
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('11111111-1111-1111-1111-111111111111'); // Le 39V par défaut

  const { data: restaurants, isLoading: loadingRestaurants } = useRestaurants();
  const { data: auditItems, isLoading: loadingAudit } = useAuditItems(selectedRestaurant);
  const { data: stats } = useAuditStats(selectedRestaurant);

  const isLoading = loadingRestaurants || loadingAudit;

  // Grouper les items par catégorie
  const techniqueItems = auditItems?.filter(i => i.category === 'technique') || [];
  const semantiqueItems = auditItems?.filter(i => i.category === 'semantique') || [];
  const localItems = auditItems?.filter(i => i.category === 'local') || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-stratos-gold animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement de l'audit...</p>
        </div>
      </div>
    );
  }

  const currentRestaurant = restaurants?.find(r => r.id === selectedRestaurant);

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="w-8 h-8 text-stratos-gold" />
            Audit SEO - Suivi des Actions
          </h1>
          <p className="text-gray-600 mt-2">
            Gérez et suivez la progression des optimisations SEO
          </p>
        </div>

        {/* Sélecteur de restaurant */}
        <div className="flex gap-2">
          {restaurants?.map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? 'default' : 'outline'}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className={
                selectedRestaurant === restaurant.id
                  ? 'bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900'
                  : ''
              }
            >
              <Building2 className="w-4 h-4 mr-2" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* KPIs */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {/* Taux de complétion */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Taux de Complétion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-gray-900">
                  {stats.completionRate}%
                </span>
                <span className="text-sm text-gray-500 mb-1">
                  ({stats.completed}/{stats.total})
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-600 transition-all duration-300"
                  style={{ width: `${stats.completionRate}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* En cours */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                En Cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-600" />
                <span className="text-3xl font-bold text-blue-600">
                  {stats.in_progress}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {stats.todo} à démarrer
              </p>
            </CardContent>
          </Card>

          {/* Items critiques */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Items Critiques (P0)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <span className="text-3xl font-bold text-red-600">
                  {stats.byPriority.P0}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                P1: {stats.byPriority.P1} • P2: {stats.byPriority.P2}
              </p>
            </CardContent>
          </Card>

          {/* Bloqués */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                Items Bloqués
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-600" />
                <span className="text-3xl font-bold text-orange-600">
                  {stats.blocked}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Nécessitent une action
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Info Restaurant */}
      {currentRestaurant && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold text-blue-900">
                {currentRestaurant.name}
              </h3>
              <p className="text-sm text-blue-700">
                {stats?.total} items d'audit • Cliquez sur les items pour les marquer comme complétés
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Listes par catégorie */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        <AuditItemsList
          items={techniqueItems}
          title="🔧 Audit Technique"
          category="technique"
        />

        <AuditItemsList
          items={semantiqueItems}
          title="📝 Audit Sémantique"
          category="semantique"
        />

        <AuditItemsList
          items={localItems}
          title="📍 Audit Local / GEO"
          category="local"
        />
      </motion.div>

      {/* Empty state */}
      {auditItems && auditItems.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Aucun item d'audit
          </h3>
          <p className="text-gray-600 mb-4">
            Importez les données d'audit pour commencer
          </p>
          <div className="bg-gray-100 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm text-gray-700 mb-2">Exécutez dans votre terminal :</p>
            <code className="block bg-white p-2 rounded text-xs">
              npm run seed:audit
            </code>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AuditPageV2;


